'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import dryBoxSurveyService from '../../services/DryBoxSurveyService.js';
import DBSFileCabinet from './DBSFileCabinet.js';
import { ClipboardCheck, Pencil, ArrowLeft, Download, CheckCircle, XCircle, FolderOpen, DollarSign } from "lucide-react";
import { getViewPageActionButtons, isViewPageActionDisabled } from '../../utils/roleBasedUIHelpers.js';
import { ROLES } from '../../utils/permissions.js';

const ViewDryBoxSurvey = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFileCabinet, setShowFileCabinet] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const userRole = user?.role_name || user?.role;
  const isClient = userRole === ROLES.CLIENT;
  const isAccountant = userRole === ROLES.ACCOUNTANT;
  const isViewOnlyRole = isClient || isAccountant;
  
  // Get action buttons based on role
  const actionButtons = getViewPageActionButtons(userRole);
  const isActionDisabled = (action) => isViewPageActionDisabled(userRole, action);

  useEffect(() => {
    fetchSurvey();
  }, [surveyId]);

  const fetchSurvey = async () => {
    try {
      const response = await dryBoxSurveyService.getById(surveyId);
      setSurvey(response.data);
    } catch (err) {
      Alert.error('Failed to load survey details');
      console.error(err);
      navigate('/dry-box-surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const blob = await dryBoxSurveyService.generateCertificate(surveyId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dry_box_survey_${(survey.container_no || survey.survey_id).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Certificate downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download certificate');
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: 'Paid',
        icon: CheckCircle
      },
      'unpaid': {
        bg: 'bg-red-100',
        text: 'text-red-700',
        label: 'Unpaid',
        icon: XCircle
      },
      'pending': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        label: 'Payment Pending',
        icon: DollarSign
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const renderStatus = (status) => {
    if (status === 'ok') {
      return (
        <span className="text-green-600 font-semibold flex items-center gap-1">
          <CheckCircle className="w-4 h-4" /> OK
        </span>
      );
    } else if (status === 'not sighted') {
      return (
        <span className="text-yellow-600 font-semibold flex items-center gap-1">
          <XCircle className="w-4 h-4" /> NOT SIGHTED
        </span>
      );
    } else {
      return (
        <span className="text-red-600 font-semibold flex items-center gap-1">
          <XCircle className="w-4 h-4" /> NOT OK
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading survey details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!survey) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <ClipboardCheck className="w-5 h-5 text-buttonBg" />
                      Dry Box Survey Details
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/dry-box-surveys')}
                    className="relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg">
                    {/* COLLAPSING BACKGROUND */}
                    <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0"/>
                    {/* ICON */}
                    <ArrowLeft className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90"/>
                    <span className="relative z-10">Back to Dry Box Survey List</span>
                  </button>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Profile Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {survey.survey_id}
                        </h2>
                        {!isClient && (
                          <div className="flex flex-wrap items-center gap-2">
                            {survey.send_to_dm && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                <CheckCircle className="w-3 h-3" />
                                Sent to Documentation Manager
                              </span>
                            )}
                            {survey.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3" />
                                Published to Client
                              </span>
                            )}
                            {!survey.send_to_dm && !survey.published && (
                              <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                <XCircle className="w-3 h-3" />
                                Draft
                              </span>
                            )}
                            {survey.payment_status && getPaymentStatusBadge(survey.payment_status)}
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        {actionButtons.view && (
                          <button
                            onClick={() => setShowFileCabinet(true)}
                            disabled={isActionDisabled('view')}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isActionDisabled('view') ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0"/>
                            <FolderOpen className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">File Cabinet</span>
                          </button>
                        )}
                        {actionButtons.downloadPDF && (
                          <button
                            onClick={handleDownloadCertificate}
                            disabled={isActionDisabled('downloadPDF') || (isClient && !survey.published)}
                            title={isClient && !survey.published ? 'PDF download is only available after publishing' : ''}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${(isActionDisabled('downloadPDF') || (isClient && !survey.published)) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0"/>
                            <Download className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">Download Certificate</span>
                          </button>
                        )}
                        {actionButtons.edit && (
                          <button
                            onClick={() => navigate(`/dry-box-surveys/edit/${surveyId}`)}
                            disabled={isActionDisabled('edit')}
                            className={`relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-white text-buttonBg font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:text-white ${isActionDisabled('edit') ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                            <span className="absolute inset-0 bg-buttonBg scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300 ease-out z-0"/>
                            <Pencil className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90" />
                            <span className="relative z-10">Edit Survey</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Survey ID</label>
                          <span className={`inline-block text-sm sm:text-base font-semibold px-0 py-1 rounded-lg ${
                            !isClient 
                              ? survey.published 
                                ? 'text-secondary' 
                                : 'text-secondary'
                              : 'text-secondary'
                          }`}>
                            {survey.survey_id}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Date of Inspection</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(survey.date_of_inspection)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Place of Inspection</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.place_of_inspection || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Overall Status</label>
                          <div className="text-sm sm:text-base">{renderStatus(survey.status)}</div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Surveyor</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.surveyor_name}</p>
                        </div>
                        {survey.location && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Location</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                              {survey.location.city}, {survey.location.state}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Container Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Container Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Container No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.container_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tare Weight (Kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.tare_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Maximum Gross Weight (Kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.maximum_gross_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Size (Feet)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.size || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CSC</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.csc || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CSC Validity</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.csc_validity || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Date of Manufacture</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(survey.date_of_mfg)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Depot Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.depot_name || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Side Inspections */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Side Inspections
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Rear End</label>
                          <div className="text-sm sm:text-base">{renderStatus(survey.rear_end)}</div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Right Side</label>
                          <div className="text-sm sm:text-base">{renderStatus(survey.right_side)}</div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Front Side</label>
                          <div className="text-sm sm:text-base">{renderStatus(survey.front_side)}</div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Left Side</label>
                          <div className="text-sm sm:text-base">{renderStatus(survey.left_side)}</div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Top Roof</label>
                          <div className="text-sm sm:text-base">{renderStatus(survey.top_roof)}</div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Under Structure</label>
                          <div className="text-sm sm:text-base">{renderStatus(survey.under_structure)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Inspection Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Inspection Details
                      </h3>
                      <div className="space-y-3">
                        {survey.interior && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Interior</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{survey.interior}</p>
                          </div>
                        )}
                        {survey.remark && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Remark</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{survey.remark}</p>
                          </div>
                        )}
                        {survey.note && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Note</label>
                            <p className="text-sm sm:text-base text-secondary whitespace-pre-wrap">{survey.note}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{survey.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(survey.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(survey.updated_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Published Status</label>
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            survey.published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {survey.published ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {survey.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* File Cabinet Modal */}
      {showFileCabinet && survey && (
        <DBSFileCabinet 
          surveyId={survey.survey_id} 
          clientId={survey.client_id} 
          onClose={() => setShowFileCabinet(false)} 
        />
      )}
    </div>
  );
};

export default ViewDryBoxSurvey;
